import { Injectable, signal, computed } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class VersionCheckService {
  private currentVersion = environment.sentryRelease;
  private enabled = /^[0-9a-f]{7,40}$/i.test(this.currentVersion);

  private _versionMismatch = signal(false);
  private _dismissed = signal(false);
  private _lastCheckTime = 0;
  private readonly CHECK_INTERVAL = 60_000;

  readonly showBanner = computed(() =>
    this._versionMismatch() && !this._dismissed()
  );

  constructor(private router: Router) {
    if (!this.enabled) {
      this.log('Version check disabled (not a valid SHA):', this.currentVersion);
      return;
    }

    this.log('Initialized with version:', this.currentVersion);
    this.subscribeRouterEvents();
    this.subscribeVisibilityChange();
  }

  dismiss(): void {
    this._dismissed.set(true);
  }

  refresh(): void {
    window.location.reload();
  }

  checkVersion(): void {
    this.fetchAndCompare();
  }

  private subscribeRouterEvents(): void {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        if (this._dismissed()) {
          this._dismissed.set(false);
        }
        this.fetchAndCompare();
      });
  }

  private subscribeVisibilityChange(): void {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.fetchAndCompare();
      }
    });
  }

  private async fetchAndCompare(): Promise<void> {
    if (this._versionMismatch() && !this._dismissed()) return;

    const now = Date.now();
    if (now - this._lastCheckTime < this.CHECK_INTERVAL) return;
    this._lastCheckTime = now;

    try {
      const response = await fetch('/version.json', { cache: 'no-store' });
      if (!response.ok) return;

      const data = await response.json();
      if (!data?.version) return;

      if (data.version !== this.currentVersion) {
        this.log('Mismatch detected:', this.currentVersion, '→', data.version);
        this._versionMismatch.set(true);
      }
    } catch {
      // silent ignore
    }
  }

  private log(...args: any[]): void {
    if (!environment.production) {
      console.log('[VersionCheck]', ...args);
    }
  }
}
